import { TmBaseComponent } from '@treasury/presentation';
import {
    circleCheckmarkFilledIcon,
    circleCheckmarkIcon,
    circleCloseFilledIcon,
    circleCloseIcon,
} from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-icon';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

type ApprovalState = 'Approved' | 'Rejected' | 'na';

export const tagName = 'jhd-approval-toggle';
@customElement(tagName)
export class JhdApprovalToggle extends TmBaseComponent {
    @property({ type: String })
    public selectedState: ApprovalState = 'na';

    @property({ type: Boolean, reflect: true, attribute: 'readonly' })
    public readonly = false;

    @property({ type: String, reflect: true })
    public indicatorBorderHeightClass = 'h-full';

    @state()
    private approveColor = 'var(--content-default)';

    @state()
    private rejectColor = 'var(--content-default)';

    protected updated(changedProps: Map<string, unknown>) {
        super.updated(changedProps);
        if (changedProps.has('selectedState')) {
            this.determineIconColor();
        }
    }

    protected render() {
        return html`<div
            class="gap-4 items-center border-l-4 flex-0 status-${this.selectedState
                ? this.selectedState.toLowerCase()
                : 'na'} ${this.indicatorBorderHeightClass} ${this.readonly ? '' : 'pl-2'}"
        >
            ${this.renderActions()}
        </div>`;
    }

    private determineIconColor() {
        if (this.selectedState === 'Approved') {
            this.approveColor = 'var(--content-positive)';
            this.rejectColor = 'var(--content-deselected)';
        }
        if (this.selectedState === 'Rejected') {
            this.approveColor = 'var(--content-deselected)';
            this.rejectColor = 'var(--content-negative)';
        }
        if (this.selectedState === 'na') {
            this.approveColor = 'var(--content-default)';
            this.rejectColor = 'var(--content-default)';
        }
    }

    private renderButton(state: 'Approved' | 'Rejected') {
        return html`<button
            class="flex justify-center items-center"
            @click=${() => {
                if (this.readonly) {
                    return;
                }
                if (this.selectedState === state) {
                    this.selectedState = 'na';
                } else {
                    this.selectedState = state;
                }
                this.determineIconColor();
                this.dispatchEvent(new CustomEvent('change', { detail: this.selectedState }));
            }}
        >
            ${this.renderIcon(state)}
        </button>`;
    }

    private renderIcon(state: 'Approved' | 'Rejected') {
        const isStateApproved = state === 'Approved';
        const approvedIcon =
            isStateApproved && this.selectedState === 'Approved'
                ? circleCheckmarkFilledIcon
                : circleCheckmarkIcon;
        const rejectedIcon =
            !isStateApproved && this.selectedState === 'Rejected'
                ? circleCloseFilledIcon
                : circleCloseIcon;
        const icon = html`<tm-icon
            width="24"
            height="24"
            .icon=${isStateApproved ? approvedIcon : rejectedIcon}
            .color=${isStateApproved ? this.approveColor : this.rejectColor}
        ></tm-icon>`;
        return this.readonly && this.selectedState !== 'na'
            ? html`<div class="flex justify-center items-center w-10 h-10">${icon}</div>`
            : icon;
    }

    private renderActions() {
        if (this.readonly && this.selectedState !== 'na') {
            return html`${this.renderIcon(this.selectedState)}`;
        }
        return html`${this.renderButton('Approved')} ${this.renderButton('Rejected')}`;
    }

    public static get styles() {
        return [
            css`
                div {
                    display: flex;
                }
                button {
                    border: none;
                }
                jhd-icon {
                    position: relative;
                }
                .status-na {
                    border-color: transparent;
                }
                .status-approved {
                    border-color: var(--content-positive);
                }
                .status-rejected {
                    border-color: var(--content-negative);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: JhdApprovalToggle;
    }
}
